import { createRef, CSSProperties, forwardRef, ReactNode } from "react";
import { Theme } from "../../config/themes";
import { Asset } from "../../types/Asset";
import classNames, { Argument } from "classnames";
import ImageOrVideo from "../ImageOrVideo";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { cmsAssetUrl } from "../../utils/CmsUtils";
import { AnimatePresence, motion } from "motion/react";
import "./style.scss";

type HeroboxMode = "normal" | "storytelling";
type HeroboxType = "fat" | "medium" | "slim" | "full";
type HeroboxProps = {
  id?: string;
  backgroundImage?: string | object;
  centerImage?: boolean;
  theme?: Theme;
  mode?: HeroboxMode;
  type?: HeroboxType;
  textShadow?: boolean;
  className?: Argument;
  style?: CSSProperties;
  deferLoading?: boolean;
  children?: ReactNode;
};

const Herobox = forwardRef<HTMLDivElement, HeroboxProps>((props, ref) => {
  ref = ref || createRef();
  const {
    backgroundImage,
    centerImage,
    theme = "black",
    mode = "normal",
    type = "fat",
    textShadow = false,
    id,
    className,
    style = {},
    deferLoading = false,
    children,
    ...rest
  } = props;
  const settings = useLocSettings();
  const defaultBgImage = settings.default_bg_image as Asset;
  const image: Partial<Asset> = backgroundImage
    ? typeof backgroundImage === "object"
      ? backgroundImage
      : { path: backgroundImage, image: true }
    : {
        ...defaultBgImage,
        path: cmsAssetUrl(defaultBgImage.path)
      };

  const classes = classNames(
    "herobox",
    "mode-" + mode,
    "theme-" + theme,
    "herobox-" + (children ? type : "slim"),
    { "text-shadow": textShadow },
    className
  );

  return (
    <div ref={ref} key={id} id={id} {...rest} className={classes} style={style}>
      <AnimatePresence initial={false}>
        <motion.div
          key={image.path}
          className="herobox-bkg-wrapper"
          transition={{
            duration: 0.75,
            ease: "easeInOut"
          }}
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.5,
              delay: 0.5
            }
          }}
        >
          {!deferLoading && (
            <ImageOrVideo
              image={image as Asset}
              className="herobox-bkg"
              centerImage={centerImage}
            />
          )}
        </motion.div>
      </AnimatePresence>
      <div className="herobox-content-wrapper">
        <div className={classNames("herobox-content")}>{children}</div>
      </div>
    </div>
  );
});
Herobox.displayName = "Herobox";

export default Herobox;
